import { Task } from "generated/graphql";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import { useMemo } from "react";
import { Cell } from "react-table";
import dayjs from "dayjs";
import { Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TaskActionsCell from "modules/tasks/TaskActionsCell/TaskActionsCell";
import TaskNotifiableIdCell from "modules/tasks/TaskNotifiableIdCell/TaskNotifiableIdCell";

import useGetMyTasks from "./useGetMyTasks";

type AgeColor = "error" | "warning" | "success";

function getAgeColor(createdAt: string): AgeColor {
  const days = dayjs().diff(dayjs(createdAt), "day");
  if (days >= 7) return "error";
  if (days >= 3) return "warning";
  return "success";
}

type Scope = {
  all?: boolean;
  userId?: string;
};

export default function useMyTasksTable(status: string, scope?: Scope) {
  const { offset, sorting } = usePaginatedVariables({
    initialSortKey: "dueAt",
    initialSortDirection: "asc",
  });

  const params = {
    page: offset.page,
    first: offset.first,
    status,
    userId: scope && "userId" in scope ? scope.userId : undefined,
    all: scope && "all" in scope ? scope.all : undefined,
  };

  const { data, loading, error, paginatorInfo, refetch } = useGetMyTasks(params);

  const pagination = usePaginatedResources({
    paginate: offset.paginate,
    paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        id: "age",
        Header: "",
        accessor: (row: Task) => row.createdAt,
        disableSortBy: true,
        width: 40,
        Cell: ({ value }: Cell<Task>) => {
          const color = getAgeColor(String(value ?? ""));
          return (
            <MDBox display="flex" alignItems="center" justifyContent="center">
              <Icon color={color} fontSize="small">
                circle
              </Icon>
            </MDBox>
          );
        },
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ value }) => dayjs(String(value ?? "")).format("YYYY-MM-DD"),
      },
      {
        Header: "Due Date",
        accessor: "dueAt",
        Cell: ({ value }) =>
          value ? dayjs(String(value)).format("YYYY-MM-DD") : "—",
      },
      {
        Header: "Type",
        accessor: "triggerType",
        Cell: ({ row }: Cell<Task>) => (
          <MDTypography variant="button" fontWeight="medium">
            {(row.original.triggerType || "—").toString()}
          </MDTypography>
        ),
      },
      {
        Header: "ID",
        // Keep accessor as notifiableId (internal) so existing behavior stays intact.
        accessor: "notifiableId",
        disableSortBy: true,
        Cell: TaskNotifiableIdCell,
      },
      {
        Header: "Assigned User",
        accessor: "assignedUser",
        disableSortBy: true,
        Cell: ({ row }: Cell<Task>) => {
          const assignedUser = row.original.assignedUser;
          const assignedUserName =
            assignedUser?.name ||
            `${assignedUser?.firstName ?? ""} ${assignedUser?.lastName ?? ""}`.trim() ||
            assignedUser?.email;

          return (
            <MDTypography variant="button">{assignedUserName || "—"}</MDTypography>
          );
        },
      },
      {
        Header: "Customer",
        accessor: "company",
        disableSortBy: true,
        Cell: ({ row }: Cell<Task>) => {
          const companyName = row.original.company?.name;
          return (
            <MDBox>
              <MDTypography variant="button" fontWeight="medium">
                {companyName || "—"}
              </MDTypography>
            </MDBox>
          );
        },
      },
      {
        Header: "Task",
        accessor: "content",
        disableSortBy: true,
        Cell: ({ value }) => (
          <MDTypography variant="button" sx={{ whiteSpace: "pre-wrap" }}>
            {String(value ?? "")}
          </MDTypography>
        ),
      },
      {
        align: "center",
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: TaskActionsCell,
      },
    ];
  }, []);

  const tableData = useMemo(() => {
    const sortKey = sorting.sortKey || "dueAt";
    const sortDirection = sorting.sortDirection === "desc" ? "desc" : "asc";
    const sortMultiplier = sortDirection === "desc" ? -1 : 1;

    const rows = [...(data || [])].sort((a, b) => {
      if (sortKey === "dueAt") {
        const aDue = a.dueAt ? dayjs(a.dueAt).valueOf() : null;
        const bDue = b.dueAt ? dayjs(b.dueAt).valueOf() : null;

        // Always keep tasks without due dates at the bottom.
        if (aDue === null && bDue === null) return 0;
        if (aDue === null) return 1;
        if (bDue === null) return -1;

        return (aDue - bDue) * sortMultiplier;
      }

      if (sortKey === "createdAt") {
        const aCreated = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const bCreated = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        return (aCreated - bCreated) * sortMultiplier;
      }

      if (sortKey === "triggerType") {
        const aType = (a.triggerType ?? "").toString().toLowerCase();
        const bType = (b.triggerType ?? "").toString().toLowerCase();
        return aType.localeCompare(bType) * sortMultiplier;
      }

      return 0;
    });

    return { columns, rows };
  }, [columns, data, sorting.sortDirection, sorting.sortKey]);

  return { loading, error, tableData, pagination, refetch, sorting } as const;
}
